import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { EventType } from '@azure/msal-browser';
import type { AuthenticationResult, EventMessage, IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { config } from '../config';
import { getMsalInstance } from './msalConfig';
import { ConfigError } from '../components/ConfigError';
import { FullPageSpinner } from '../components/ui/Spinner';

/**
 * Initialises MSAL once, pins the active account after a redirect login, then mounts MsalProvider.
 * The redirect response itself is processed by msal-react (handleRedirectPromise).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [pca, setPca] = useState<IPublicClientApplication | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!config.authConfigured) return;
    let cancelled = false;
    let registered: { instance: IPublicClientApplication; callbackId: string | null } | null = null;
    getMsalInstance()
      .then((instance) => {
        if (cancelled) return;
        const accounts = instance.getAllAccounts();
        if (!instance.getActiveAccount() && accounts[0]) instance.setActiveAccount(accounts[0]);
        const callbackId = instance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const result = event.payload as AuthenticationResult;
            if (result.account) instance.setActiveAccount(result.account);
          }
        });
        registered = { instance, callbackId };
        setPca(instance);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e : new Error(String(e))));
    return () => {
      cancelled = true;
      if (registered?.callbackId) registered.instance.removeEventCallback(registered.callbackId);
    };
  }, []);

  if (!config.authConfigured) {
    return (
      <ConfigError
        title="Sign-in is not configured"
        detail="VITE_ENTRA_CLIENT_ID is empty. Copy packages/client/.env.example to .env and set the app registration values."
      />
    );
  }
  if (error) return <ConfigError title="Sign-in failed to initialise" detail={error.message} />;
  if (!pca) return <FullPageSpinner label="Starting sign-in" />;
  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}
